import { useBookingPaymentAccounts } from '@/api/bookings';
import { AccountChoice } from '@/components/AccountChoice';
import { useState } from 'react';

import type { Booking, ChangeBookingStageInput, PaymentMethod } from '@/api/bookings';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import {
  cancelLabel,
  cancelMoney,
  cancellationMoneyInput,
  cancellationMovesMoney,
  suggestedKeep,
} from '@core/bookings/cancellation';
import { depositPaidBy, everydayMethods } from '@core/bookings/payment';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import {
  defaultHoldHours,
  holdHoursProblem,
  stageMoveInput,
  type StageMove,
} from '@core/bookings/stageMoves';
import { formatAmount } from '@core/format/money';

import { MoneyLine, MoneyLines } from './MoneyLine';
import { Choice, Dialog, Group } from './Dialog';

/**
 * One decision about a booking — hold, expire, cancel or recover — said in words before it is made,
 * then sent: the phone's `app/booking/stage.tsx` on the website.
 *
 * <p>
 * A hold asks how long; a cancellation and a recovery ask why, which is what a shop reads back when
 * the customer disputes it. <strong>A cancellation with money on it says what happens to the
 * money</strong> (owner, 2026-10-15): the shop keeps the token by default — changeable down to
 * nothing — and the rest of the rent and the whole deposit go back, the button naming the total. The
 * rules are the core's `bookings/cancellation`; the server invoices what is kept.
 * </p>
 */
export function StageDialog({
  booking,
  move,
  currency,
  busy,
  onSend,
  onClose,
}: {
  booking: Booking;
  move: StageMove;
  currency: string;
  busy: boolean;
  onSend: (input: ChangeBookingStageInput) => void;
  onClose: () => void;
}) {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const [hours, setHours] = useState(String(defaultHoldHours));
  const [reason, setReason] = useState('');
  // Untouched, the keep box shows the token; once typed in, what was typed.
  const [keepText, setKeepText] = useState<string | null>(null);
  const [refundMethod, setRefundMethod] = useState<PaymentMethod>(() => depositPaidBy(booking));
  // Which account the refunds leave (split payments step 6); null is the method's default.
  const [refundAccount, setRefundAccount] = useState<number | null>(null);
  const accounts = useBookingPaymentAccounts(booking.branchId ?? undefined);

  const holdHours = Number(hours);
  const hoursProblem = move.to === 'Hold' ? holdHoursProblem(holdHours) : null;
  const money =
    move.to === 'Cancelled' && cancellationMovesMoney(booking)
      ? cancelMoney(booking, keepText ?? String(suggestedKeep(booking)), currency)
      : null;
  const blocked = busy || hoursProblem !== null || (money?.problem ?? null) !== null;

  const send = () => {
    if (blocked) {
      return;
    }

    onSend({
      ...stageMoveInput(move, { reason, holdHours }),
      ...(money ? { money: cancellationMoneyInput(money, refundMethod, refundAccount) } : {}),
    });
  };

  return (
    <Dialog
      title={move.label}
      subtitle={booking.customerName}
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button
            variant={move.to === 'Cancelled' ? 'danger' : 'primary'}
            shortcut="save"
            className="ms-auto"
            disabled={blocked}
            onClick={send}
          >
            {money ? cancelLabel(money, currency) : move.label}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-gray-900">{move.consequence}</p>

      {move.to === 'Hold' && (
        <Field
          label="Hold for how many hours"
          hint={hoursProblem ?? 'At most a week. It lapses on its own after that.'}
        >
          <input
            type="number"
            min={1}
            autoFocus
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            onWheel={(event) => event.currentTarget.blur()}
            className={controlClass}
          />
        </Field>
      )}

      {move.asksReason && (
        <Field
          label="Why"
          hint="Kept on the booking's history — what the shop reads back if it is disputed."
        >
          <textarea
            rows={2}
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className={textAreaClass}
          />
        </Field>
      )}

      {money && (
        <div className="border-border-color rounded border bg-light p-3">
          <p className="mb-1 text-[12px] font-semibold tracking-wide text-gray-900 uppercase">
            The money
          </p>
          <MoneyLines>
            <MoneyLine label="Rent paid" value={cash(money.paid)} />
          </MoneyLines>

          {money.paid > 0 && (
            <div className="my-2">
              <Field
                label="The shop keeps"
                hint={`The token (${cash(suggestedKeep(booking))}) by default. Type 0 to give everything back. Billed as the cancellation charge.`}
              >
                <input
                  type="number"
                  min={0}
                  value={keepText ?? String(suggestedKeep(booking))}
                  onChange={(event) => setKeepText(event.target.value)}
                  onWheel={(event) => event.currentTarget.blur()}
                  className={controlClass}
                />
              </Field>
            </div>
          )}

          {money.problem && <Notice tone="warning" title={money.problem} />}

          <MoneyLines>
            {money.paid > 0 && <MoneyLine label="Rent given back" value={cash(money.rentBack)} />}
            {money.depositBack > 0 && (
              <MoneyLine label="Deposit given back" value={cash(money.depositBack)} />
            )}
            <MoneyLine label="Give back in all" value={cash(money.totalBack)} strong />
          </MoneyLines>

          {money.totalBack > 0 && (
            <div className="mt-2">
              <Group label="Given back by">
                <div className="flex flex-wrap gap-1.5">
                  {everydayMethods.map((method) => (
                    <Choice
                      key={method}
                      label={paymentMethodLabel[method]}
                      chosen={refundMethod === method}
                      onChoose={() => {
                        setRefundMethod(method);
                        setRefundAccount(null);
                      }}
                    />
                  ))}
                </div>
              </Group>
              <div className="mt-2">
                <AccountChoice
                  method={refundMethod}
                  accounts={accounts.data}
                  value={refundAccount}
                  onChange={setRefundAccount}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
